"use client";

import type {
  WriterAdapterMode,
  WriterProjectSourceType,
  WriterProjectSummary,
} from "./types";

const STATE_EVENT = "findin-writer:sidebar-state";
const ACTION_EVENT = "findin-writer:sidebar-action";
const STATE_STORAGE_KEY = "findin:writer:sidebar-state";
const ACTION_STORAGE_KEY = "findin:writer:sidebar-action";

export type WriterSidebarState = {
  activeProjectId?: string;
  busy: boolean;
  projects: WriterProjectSummary[];
  switchingProjectId?: string;
};

export type WriterSidebarAction =
  | { type: "create" }
  | { projectId: string; type: "delete" }
  | { projectId: string; title: string; type: "rename" }
  | { projectId: string; type: "switch" };

const emptyState: WriterSidebarState = { busy: false, projects: [] };

export function isProtectedWriterFixtureProject(projectId: string) {
  return projectId.startsWith("writer-fixture-");
}

export function shouldNavigateAfterWriterProjectDeletion(projectId: string, activeProjectId?: string) {
  return projectId === activeProjectId;
}

export function isWriterProjectDeletionBlocked(input: {
  adapterMode: WriterAdapterMode;
  projectId: string;
  sourceType?: WriterProjectSourceType;
}) {
  return input.adapterMode === "http"
    && (input.sourceType === "summary" || /^s\d+$/.test(input.projectId));
}

export function isWriterProjectMutationBlockedByAgent(input: {
  activeProjectId?: string;
  activeRunStatus?: string;
  agentRunning: boolean;
  projectId: string;
}) {
  return input.projectId === input.activeProjectId
    && (input.agentRunning || input.activeRunStatus === "running");
}

function parseJson<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function readWriterSidebarState(): WriterSidebarState {
  if (typeof window === "undefined") return emptyState;
  return parseJson<WriterSidebarState>(window.sessionStorage.getItem(STATE_STORAGE_KEY)) ?? emptyState;
}

export function publishWriterSidebarState(state: WriterSidebarState) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(state));
  window.dispatchEvent(new CustomEvent<WriterSidebarState>(STATE_EVENT, { detail: state }));
}

export function subscribeWriterSidebarState(listener: (state: WriterSidebarState) => void) {
  if (typeof window === "undefined") return () => undefined;
  const handleState = (event: Event) => listener((event as CustomEvent<WriterSidebarState>).detail);
  window.addEventListener(STATE_EVENT, handleState);
  listener(readWriterSidebarState());
  return () => window.removeEventListener(STATE_EVENT, handleState);
}

export function requestWriterSidebarAction(action: WriterSidebarAction) {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(ACTION_STORAGE_KEY, JSON.stringify(action));
  window.dispatchEvent(new CustomEvent<WriterSidebarAction>(ACTION_EVENT, { detail: action }));
}

export function subscribeWriterSidebarAction(listener: (action: WriterSidebarAction) => void) {
  if (typeof window === "undefined") return () => undefined;
  const consume = (action: WriterSidebarAction | null) => {
    if (!action) return;
    window.sessionStorage.removeItem(ACTION_STORAGE_KEY);
    listener(action);
  };
  const handleAction = (event: Event) => consume((event as CustomEvent<WriterSidebarAction>).detail);
  window.addEventListener(ACTION_EVENT, handleAction);
  consume(parseJson<WriterSidebarAction>(window.sessionStorage.getItem(ACTION_STORAGE_KEY)));
  return () => window.removeEventListener(ACTION_EVENT, handleAction);
}
